import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import apiService, { Platform } from '../services/api';
import useAccounts, { Account } from './useAccounts';
import { logger } from '../utils/logger';

export interface UseCreateSimulationDataReturn {
  platforms: Platform[];
  loading: boolean;
  editMode: {id: string} | null;
  initialDataFetched: boolean;
  setLoading: (loading: boolean) => void;
  setEditMode: (editMode: {id: string} | null) => void;
}

export const useCreateSimulationData = (
  dispatch: any,
  handleTrialApiError: (error: any) => boolean,
  setAlertMessage: (message: any) => void
): UseCreateSimulationDataReturn => {
  const location = useLocation();
  const { accounts, loading: accountsLoading } = useAccounts();

  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState<{id: string} | null>(null);
  const [initialDataFetched, setInitialDataFetched] = useState(false);

  // Use ref to track if we've already dispatched - prevents StrictMode double-run
  const hasDispatchedRef = useRef(false);

  // Extract simulation ID from URL if present (for editing)
  const searchParams = new URLSearchParams(location.search);
  const simulationId = searchParams.get('id');
  const fromIdeaDraft = searchParams.get('fromIdeaDraft');

  // SINGLE EFFECT for loading ALL data
  useEffect(() => {
    // Guard: Don't run if already dispatched or accounts still loading
    if (hasDispatchedRef.current || accountsLoading) {
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);

        // Always fetch platforms
        const platformsData = await apiService.getPlatforms();
        setPlatforms(platformsData);

        // EDIT MODE: Load simulation data
        if (simulationId) {
          try {
            const response = await apiService.getSimulation(simulationId);
            const simulation = response.simulation;

            setEditMode({ id: simulationId });

            // Prepare ALL form data before dispatching
            let imageFile: File | null = null;
            let imagePreviewUrl = '';
            let mediaDescription = '';
            let accountId = '';
            let sector = simulation.sector || '';

            // Handle image - check for imageUrl directly (hasImage may be undefined)
            if (simulation.imageUrl) {
              // Set preview URL immediately - this is the primary display method
              imagePreviewUrl = simulation.imageUrl;

              // Try to download image for File object (optional, for re-upload)
              try {
                const imageResponse = await fetch(simulation.imageUrl);
                const blob = await imageResponse.blob();
                const fileName = simulation.imageUrl.split('/').pop() || 'image.jpg';
                imageFile = new File([blob], fileName, { type: blob.type });
              } catch (err) {
                logger.warn("Could not download image file, using URL preview instead");
                // imageFile stays null, imagePreviewUrl is still set
              }
            } else if (simulation.resultJson?.imageDescription) {
              // No image URL - use media description if available
              const desc = simulation.resultJson.imageDescription;
              mediaDescription = typeof desc === 'string' ? desc : desc?.alt || '';
            }

            // Handle account matching - do it NOW, not in a separate effect
            if (simulation.accountId && accounts.length > 0) {
              const matchedAccount = accounts.find(acc => acc.id === simulation.accountId);
              if (matchedAccount) {
                accountId = matchedAccount.id;
                sector = typeof matchedAccount.sector === 'object'
                  ? (matchedAccount.sector as any)?.name || ''
                  : matchedAccount.sector || '';
              }
            } else if (simulation.sender && accounts.length > 0) {
              const matchedAccount = accounts.find(acc => acc.name === simulation.sender);
              if (matchedAccount) {
                accountId = matchedAccount.id;
                sector = typeof matchedAccount.sector === 'object'
                  ? (matchedAccount.sector as any)?.name || ''
                  : matchedAccount.sector || '';
              }
            }

            // SINGLE DISPATCH with ALL data
            hasDispatchedRef.current = true;
            dispatch({
              type: 'UPDATE_FORM',
              payload: {
                content: simulation.content || '',
                platform: simulation.platform || 'Twitter/X',
                accountId,
                sector,
                simCount: simulation.simCount || 50,
                imageFile,
                imagePreviewUrl,
                imageDescription: mediaDescription,
                isPrivate: simulation.isPrivate || false
              }
            });

            setInitialDataFetched(true);

          } catch (error) {
            if (!handleTrialApiError(error)) {
              logger.error('Error fetching simulation:', error);
              setAlertMessage({
                type: 'error',
                message: 'Failed to load simulation. Please try again.'
              });
            }
          }
        }

        // IDEA DRAFT PREFILL MODE
        else if (fromIdeaDraft || (location.state as any)?.prefill) {
          try {
            const prefill = (location.state as any)?.prefill || {};
            let accountId = '';
            let sector = prefill.sector || '';

            if (prefill.brand && accounts.length > 0) {
              const matchedAccount = accounts.find(acc => acc.name === prefill.brand);
              if (matchedAccount) {
                accountId = matchedAccount.id;
                sector = typeof matchedAccount.sector === 'object'
                  ? (matchedAccount.sector as any)?.name || ''
                  : matchedAccount.sector || '';
              }
            }

            hasDispatchedRef.current = true;
            dispatch({
              type: 'UPDATE_FORM',
              payload: {
                content: prefill.content || '',
                platform: prefill.platform || 'Twitter/X',
                accountId,
                sector,
                imageDescription: prefill.mediaDescription || '',
                // @ts-ignore
                ideaDraftId: prefill.ideaDraftId || fromIdeaDraft || ''
              }
            });

            setInitialDataFetched(true);
          } catch (e) {
            logger.error('Failed to prefill from idea draft:', e);
          }
        }

      } catch (error) {
        if (!handleTrialApiError(error)) {
          logger.error('Error fetching data:', error);
          setAlertMessage({
            type: 'error',
            message: 'Failed to load form data. Please refresh the page.'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();

  }, [accountsLoading, accounts, simulationId, fromIdeaDraft, location.state, dispatch, handleTrialApiError, setAlertMessage]);

  return {
    platforms,
    loading,
    editMode,
    initialDataFetched,
    setLoading,
    setEditMode
  };
};
